import React from "react";
import LegalLayout, { H2, H3, P, UL } from "./LegalLayout";
import SEO from "../components/SEO";

const UPDATED = "13 octombrie 2026";

export default function PrivacyPolicy() {
  return (
    <>
      <SEO
        title="Politica de confidențialitate | FPED"
        description="Politica de confidențialitate FPED — modul în care colectăm, utilizăm și protejăm datele personale pe ai.fped.org."
        canonical="https://ai.fped.org/politica-confidentialitate"
      />
      <LegalLayout
        title="Politica de confidențialitate"
        lastUpdated={UPDATED}
        testid="privacy-policy-page"
        intro={`Această politică explică modul în care Fundația pentru Educație Digitală colectează, utilizează și protejează datele tale cu caracter personal atunci când interacționezi cu landing page-ul ai.fped.org și cu programul de formare «Introducere în Inteligența Artificială pentru educație».`}
    >
      <H2>1. Operatorul de date</H2>
      <P>
        Operatorul datelor cu caracter personal este <strong>Fundația pentru Educație Digitală</strong>,
        organizație neguvernamentală cu sediul în București, Sectorul 1, Str. Ioan Bianu Nr. 27,
        înregistrată cu CIF 43506207 (denumită în continuare „Fundația”, „noi” sau „operatorul”).
      </P>
      <P>
        Pentru orice solicitare privind prelucrarea datelor tale, ne poți contacta la
        adresa <a href="mailto:contact@fped.org" className="text-[#965EA7] underline">contact@fped.org</a>.
      </P>

      <H2>2. Categorii de date colectate</H2>
      <UL>
        <li><strong>Date de identificare</strong>: nume, prenume.</li>
        <li>
          <strong>Cod numeric personal (CNP), inițiala tatălui și funcția didactică</strong>:
          colectate <strong>doar de la participanții înscriși la programul acreditat</strong>,
          după confirmarea plății, printr-un formular securizat trimis pe email. Sunt necesare
          pentru dosarul programului depus la Ministerul Educației și Cercetării (acreditare
          prin OMEC nr. 4712/19.08.2026) și <strong>nu sunt folosite în niciun alt scop</strong> —
          nu sunt transmise procesatorului de plăți, platformei de facturare sau
          instrumentelor de marketing.
        </li>
        <li><strong>Date de contact</strong>: adresă de email, număr de telefon.</li>
        <li><strong>Date profesionale</strong>: specializare didactică, unitate de învățământ.</li>
        <li><strong>Cod de recomandare</strong>: dacă te înscrii cu un cod de recomandare, reținem legătura dintre înscrierea ta și cea a cursantului care te-a recomandat, pentru acordarea beneficiului.</li>
        <li><strong>Date privind plata</strong>: doar la finalizarea unei comenzi. Plățile sunt procesate exclusiv de furnizorii externi autorizați <strong>Netopia Payments</strong> sau <strong>EuPlătesc</strong> (în funcție de provider-ul selectat la momentul tranzacției); aplicația noastră nu stochează datele cardului bancar.</li>
        <li><strong>Date tehnice</strong>: adresă IP, tip dispozitiv, browser, sistem de operare, pagini vizitate, parametri de marketing (UTM) — colectate la nivel agregat și doar cu consimțământul tău (cookies de analiză și marketing).</li>
      </UL>

      <H2>3. Scopurile prelucrării și temeiurile legale</H2>
      <UL>
        <li><strong>Înscriere și livrarea cursului</strong> — temei: executarea contractului (art. 6 alin. (1) lit. b GDPR).</li>
        <li><strong>Comunicare comercială și remarketing</strong> — temei: consimțământul tău (art. 6 alin. (1) lit. a GDPR), pe care îl poți retrage oricând.</li>
        <li><strong>Facturare și obligații fiscale/contabile</strong> — temei: îndeplinirea obligațiilor legale (art. 6 alin. (1) lit. c GDPR).</li>
        <li>
          <strong>Întocmirea dosarului programului acreditat</strong> (CNP, inițiala tatălui,
          funcția didactică) — temei: îndeplinirea unei obligații legale
          (art. 6 alin. (1) lit. c GDPR), coroborat cu art. 4 din Legea nr. 190/2018 privind
          prelucrarea codului numeric personal. Aceste date nu se prelucrează pe bază de
          consimțământ, deci nu pot fi retrase separat de retragerea din program.
        </li>
        <li><strong>Analiză și îmbunătățirea experienței</strong> — temei: consimțământ pentru cookies non-esențiale (art. 6 alin. (1) lit. a GDPR).</li>
        <li><strong>Apărarea drepturilor în caz de litigiu</strong> — temei: interes legitim (art. 6 alin. (1) lit. f GDPR).</li>
      </UL>

      <H2>4. Perioada de stocare</H2>
      <UL>
        <li>Date de lead (formulare de interes): maximum <strong>3 ani</strong> de la ultima interacțiune sau până la retragerea consimțământului.</li>
        <li>Date privind achizițiile și facturarea: <strong>10 ani</strong>, conform legislației contabile române (Legea 82/1991).</li>
        <li>
          Date pentru dosarul programului acreditat (inclusiv CNP): pe durata prevăzută
          de reglementările privind arhivarea dosarelor de formare continuă, apoi se șterg.
          Accesul la CNP în platformă este restrâns și fiecare consultare este înregistrată.
        </li>
        <li>Date din cookies de analiză/marketing: maximum <strong>24 luni</strong>, conform setărilor furnizorilor.</li>
      </UL>

      <H2>5. Destinatari și împuterniciți</H2>
      <P>
        Pentru a furniza serviciul, putem transmite date către următorii furnizori, care acționează
        în calitate de împuterniciți și au obligații contractuale stricte de protecție a datelor:
      </P>
      <UL>
        <li><strong>Vercel Inc.</strong> (SUA) — găzduire frontend (ai.fped.org). Transfer pe baza Clauzelor Contractuale Standard (SCC) și Data Privacy Framework.</li>
        <li><strong>Render Services Inc.</strong> (UE — Frankfurt) — găzduire backend API.</li>
        <li><strong>MongoDB Atlas</strong> (UE — Frankfurt) — stocare date lead-uri și comenzi.</li>
        <li><strong>Resend Inc.</strong> (SUA) — trimiterea emailurilor tranzacționale (confirmare lead, confirmare plată, înscrieri webinar) și de informare. Transfer pe baza SCC.</li>
        <li><strong>NETOPIA Financial Services S.A.</strong> (România) — procesare plăți cu cardul (Netopia Payments), dacă acesta este provider-ul activ la momentul tranzacției.</li>
        <li><strong>EuPlătesc.ro (EuroPayment Services S.R.L.)</strong> (România) — procesare plăți cu cardul, dacă acesta este provider-ul activ la momentul tranzacției.</li>
        <li>
          <strong>Ministerul Educației și Cercetării</strong> — datele din dosarul programului
          acreditat, la depunerea acestuia.
        </li>
        <li>
          <strong>Organizații partenere</strong> care organizează grupe în cadrul programului —
          au acces exclusiv la datele participanților din grupele proprii, în baza unui acord de
          prelucrare, și doar în scopul întocmirii dosarului de acreditare.
        </li>
        <li><strong>Google Ireland Ltd.</strong> — Google Analytics 4 și Google Ads, pe bază de consimțământ.</li>
        <li><strong>Meta Platforms Ireland Ltd.</strong> — Meta Pixel, pe bază de consimțământ.</li>
        <li><strong>Microsoft Ireland Operations Ltd.</strong> — Microsoft Clarity (analiză comportamentală, heatmaps și înregistrări de sesiune), activat doar după acceptarea cookie-urilor de analiză. Datele introduse în formulare și zonele sensibile (checkout, pagini de plată, dashboard administrativ) sunt mascate la sursă. Politica Microsoft: <a className="text-[#965EA7] underline" href="https://privacy.microsoft.com/privacystatement" target="_blank" rel="noopener noreferrer">privacy.microsoft.com</a>.</li>
        <li><strong>Cloudflare Inc.</strong> — protecție anti-spam (Turnstile) și DNS.</li>
        <li><strong>Autorități publice</strong>, doar la solicitare legală.</li>
      </UL>

      <H2>6. Transferuri internaționale</H2>
      <P>
        Unele dintre serviciile menționate mai sus pot implica transferuri de date în afara
        Spațiului Economic European (în special SUA). Aceste transferuri se realizează exclusiv
        pe baza unor garanții adecvate, cum sunt Clauzele Contractuale Standard aprobate de
        Comisia Europeană și/sau Data Privacy Framework UE-SUA.
      </P>

      <H2>7. Drepturile tale</H2>
      <P>În calitate de persoană vizată, ai următoarele drepturi:</P>
      <UL>
        <li>dreptul de acces la datele tale;</li>
        <li>dreptul la rectificare în cazul în care datele sunt inexacte sau incomplete;</li>
        <li>dreptul la ștergere („dreptul de a fi uitat”), în condițiile legii;</li>
        <li>dreptul la restricționarea prelucrării;</li>
        <li>dreptul la portabilitatea datelor;</li>
        <li>dreptul de a te opune prelucrării bazate pe interes legitim sau marketing direct;</li>
        <li>dreptul de a-ți retrage consimțământul oricând, fără ca acest lucru să afecteze legalitatea prelucrării anterioare;</li>
        <li>dreptul de a depune o plângere la <strong>Autoritatea Națională de Supraveghere a Prelucrării Datelor cu Caracter Personal (ANSPDCP)</strong> — B-dul G-ral. Gheorghe Magheru 28-30, București, www.dataprotection.ro.</li>
      </UL>

      <H2>8. Securitatea datelor</H2>
      <P>
        Aplicăm măsuri tehnice și organizatorice rezonabile (TLS, controlul accesului bazat pe
        roluri, hash-uire criptografică a parolelor, jurnalizare, copii de siguranță) pentru a
        proteja datele tale împotriva accesului neautorizat, modificării sau distrugerii.
      </P>

      <H2>9. Decizii automate și profilare</H2>
      <P>
        Nu luăm decizii automate care să producă efecte juridice sau să te afecteze semnificativ
        în alt mod, fără intervenție umană.
      </P>

      <H2>10. Modificări ale prezentei politici</H2>
      <P>
        Putem actualiza periodic această politică. Versiunea curentă este afișată întotdeauna pe
        această pagină, împreună cu data ultimei actualizări. Pentru modificări semnificative te
        vom anunța prin email sau printr-o notificare vizibilă pe site.
      </P>

      <H2>11. Contact pentru solicitări GDPR</H2>
      <P>
        Pentru exercitarea drepturilor tale sau pentru orice întrebare privind prelucrarea
        datelor, ne poți contacta la <a href="mailto:contact@fped.org" className="text-[#965EA7] underline">contact@fped.org</a>.
        Vom răspunde în termen de cel mult 30 de zile, conform GDPR.
      </P>
    </LegalLayout>
    </>
  );
}
